'use client';

import {
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useState,
  type FC,
  useRef,
} from 'react';
import { Flex, InputChips, type ResultListItem } from '@/_components';
import { BulkTypeSelector } from './components/BulkTypeSelector';
import { Company } from '@/_types/models/company';
import { getClasses } from '@/_utils/classnames';
import { DEFAULT_V2_ENTITY_FILTER } from '@/_constants/entities';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  getAdminEntities,
  getAdminEntitiesByIds,
} from '@/_api/v2/admin-entities';
import { UserContext } from '@/_contexts/UserProvider';
import type { EntitySelectorInputChipsProps } from './EntitySelectorInputChips.types';
import { useDebounce } from '@/_hooks/useDebounce';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';

const COMPANY_SEARCH_DEBOUNCE = 160 as const;

export const EntitySelectorInputChips: FC<EntitySelectorInputChipsProps> = ({
  hideDisabledEntities,
  bulkSelectedEntityTypes,
  setBulkSelectedEntityTypes,
  id,
  isBulkTypeSelectionEnabled,
  isDisabled,
  onClearBulkSelection,
  isLabelHidden,
  isSingleSelection,
  label,
  onChipsChange,
  preselectedEntityIds = [],
}) => {
  const { addToast } = useContext(ToastContext);
  const { isAdvisor, token } = useContext(UserContext);
  const { entityTypeCounts } = useContext(CompanyContext);

  const entitySelectorRef = useRef<HTMLDivElement | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [chips, setChips] = useState<Set<string>>(
    new Set(preselectedEntityIds.map((x) => x.toString())),
  );
  const [chipText, setChipText] = useState<string>('');
  const [chipNames, setChipNames] = useState<Array<ResultListItem>>([]);

  const entitySelectorInputClasses: string = getClasses('wp-100', id);

  const debouncedSearchValue = useDebounce(chipText, COMPANY_SEARCH_DEBOUNCE);

  const fetchCompanies = useCallback(
    async (search: string = '') => {
      if (!token) return;

      try {
        setIsLoading(true);
        const response = await getAdminEntities({
          ...DEFAULT_V2_ENTITY_FILTER,
          token,
          rows_per_page: 10,
          descending: true,
          page: 0,
          search_term: search || '',
        });
        setCompanies(response);
      } catch (error) {
        addToast('error', 'Failed to fetch companies', { error });
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, token],
  );

  const filteredCompanies: Array<Company> = useMemo(() => {
    return (
      companies?.filter((company) =>
        hideDisabledEntities
          ? !company?.inactive &&
            !company?.readonly_access &&
            (company?.user?.paying ||
              company?.user?.preferences?.has_temp_email)
          : true,
      ) || []
    );
  }, [companies, hideDisabledEntities]);

  const results: Array<ResultListItem> = useMemo(() => {
    return filteredCompanies?.map((company) => ({
      name: company.name,
      value: company.id.toString(),
      isDisabled: company?.inactive,
      label:
        (company?.inactive && 'Inactive') ||
        (company?.user?.preferences?.has_temp_email && 'No email') ||
        undefined,
    }));
  }, [filteredCompanies]);

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const onChipSelect = (chips: Set<string>) => {
    const uniqueChips: Array<number> = Array.from(
      new Set(Array.from(chips, Number)),
    );

    const existingChipNames = chipNames.filter(
      (chip) => chip.value && chips.has(chip.value),
    );

    const newChipNames = results.filter(
      (result) =>
        result.value &&
        chips.has(result.value) &&
        !existingChipNames.some((chip) => chip.value === result.value),
    );

    setChipNames([...existingChipNames, ...newChipNames]);

    return onChipsChange(uniqueChips);
  };

  const isBulkEnabled: boolean = Boolean(
    isBulkTypeSelectionEnabled &&
      !isSingleSelection &&
      bulkSelectedEntityTypes &&
      setBulkSelectedEntityTypes,
  );

  useEffect(() => {
    if (!token) return;
    if (!isAdvisor) return;

    fetchCompanies(debouncedSearchValue);
  }, [fetchCompanies, debouncedSearchValue, isAdvisor, addToast, token]);

  useEffect(() => {
    if (!token || !chips?.size) return;

    const selectedCompanyIds: Array<number> = [...chips].map((c) =>
      parseInt(c),
    );

    const _handleIncomingEntityIds = async () => {
      if (!token || !selectedCompanyIds?.length) return;

      try {
        const companies = await getAdminEntitiesByIds({
          token,
          ids: selectedCompanyIds,
        });

        if (companies?.error || !Array.isArray(companies)) {
          throw new Error('failed to fetch entities');
        }

        const newChipNames: Array<ResultListItem> = companies?.map(
          (company) => ({
            name: company.name,
            value: company.id.toString(),
          }),
        );

        setChipNames(newChipNames);
      } catch (error) {
        console.error(error);
      }
    };

    if (selectedCompanyIds?.length) {
      _handleIncomingEntityIds();
    }
  }, [chips, token]);

  useEffect(() => {
    if (!token) return;

    const _handleIncomingEntityIds = async () => {
      const newEntityChips: Set<string> = new Set(
        preselectedEntityIds.map((x) => x.toString()),
      );

      setChips(newEntityChips);
    };

    if (preselectedEntityIds?.length) {
      _handleIncomingEntityIds();
    }
  }, [preselectedEntityIds, token]);

  return (
    <Flex
      isColumn={true}
      gap={4}
      className={entitySelectorInputClasses}
      ref={entitySelectorRef}
    >
      <InputChips
        chipNames={chipNames}
        onClearChips={onClearBulkSelection}
        chips={chips}
        chipsLimit={isSingleSelection ? 1 : undefined}
        chipExtraCount={
          bulkSelectedEntityTypes?.length
            ? bulkSelectedEntityTypes.reduce(
                (sum, type) => sum + (entityTypeCounts[type] ?? 0),
                0,
              )
            : undefined
        }
        clearable={isBulkTypeSelectionEnabled}
        isDisabled={isDisabled}
        isLabelHidden={isLabelHidden}
        isLoading={isLoading}
        label={label}
        onChange={onChipText}
        onChipSelect={onChipSelect}
        placeholder="Start typing name"
        results={results}
        setChips={setChips}
        value={chipText}
      />
      {isBulkEnabled && (
        <BulkTypeSelector
          entityTypeCounts={entityTypeCounts}
          isDisabled={!!chips.size}
          selectedTypes={bulkSelectedEntityTypes}
          setSelectedTypes={setBulkSelectedEntityTypes}
        />
      )}
    </Flex>
  );
};
